import { Logger, safeJsonParse, TypedEventTarget, deepMerge } from '@halo/utils';
import { debounce } from 'lodash-es';
import type { ApiServiceType } from '../apis';
import ApiService from '../apis';
import { formatUser, isSessionCategory } from '../utils';
import type {
  WasmSdkFnKeys,
  WasmSdkArgsMap,
  WorkerCallbackResponse,
  SdkMethod,
  OriginalNotifyResponse,
  OriginalSession,
  OriginalMsg,
} from '../sdk';
import { MessageEditStatus, SessionCategoryPrefix, SessionOperate } from '../sdk';
import { MsgFileCache } from '../file';
import type {
  ClientConfig,
  ClientOptions,
  ClientInitOptions,
  NoticeEvents,
  ClientEvents,
  WasmSdkResultMap,
  Msg,
  Session,
} from './types';
import { ServerTime } from './ServerTime';

export class Client extends TypedEventTarget<NoticeEvents & ClientEvents> {
  static AbortError = class extends Error {
    constructor(message?: string) {
      super(message || 'Aborted.');
    }
  };
  private config: ClientConfig;
  private worker: Worker | null = null;
  private callbackId = 0;
  private callbackMap: Map<
    string,
    [handler: (data: WorkerCallbackResponse) => void, abortSignal?: AbortSignal]
  >;
  private initPromise: Promise<void> | null = null;
  private logger: Logger;
  private mergedSessionData: Record<string, Session> = {};
  private mergedMessageData: Record<string, Msg> = {};
  userId: string;
  Api: ApiServiceType;
  serverTime: ServerTime;
  constructor(options: ClientOptions) {
    super();
    this.config = {
      debugLevel: 0,
      rootPath: '',
      userCacheCleanupInterval: 5 * 60 * 1000, // Default 5 minutes
      ...options,
    };
  }

  async init(options: ClientInitOptions) {
    const config = { ...this.config, ...options };
    this.userId = config.userId;
    this.logger = new Logger('Client', Boolean(config.debugLevel));
    this.callbackMap = new Map();
    this.worker = new Worker(new URL('../sdk/worker.ts', import.meta.url), { type: 'module' });
    this.Api = ApiService(this);
    this.worker.onmessage = this.onMessage.bind(this);
    // this.worker.onerror = this.onError.bind(this);

    this.initPromise = this.sendWorkerMessage('sdk_init', config);
    await this.initPromise;
    this.initPromise = null;

    this.serverTime = new ServerTime(() => this.invoke('nowMillisecond'));

    const res = await Promise.allSettled([
      this.invoke('uploadSignalPackage'),
      this.invoke('uploadGroupPackage'),
    ]);

    return res;
  }

  private dispatchSessionData = debounce(() => {
    Object.entries(this.mergedSessionData).forEach(([chat_id, session]) => {
      this.dispatchEvent('UpdatePartialSession', session);
      delete this.mergedSessionData[chat_id];
    });
  }, 500);

  private dispatchMessageData = debounce(() => {
    Object.entries(this.mergedMessageData).forEach(([cli_msg_id, message]) => {
      this.dispatchEvent('UpdatePartialMessage', message as any);
      delete this.mergedMessageData[cli_msg_id];
    });
  }, 500);

  private onMessage(e: MessageEvent<WorkerCallbackResponse | OriginalNotifyResponse>) {
    const formatMessage = this.formatMessage.bind(this);
    const formatSession = this.formatSession.bind(this);
    const eventData = e.data as any; // TODO amo: Optimize OriginalNotifyResponse type
    const isCallback = 'cbId' in eventData;
    if (isCallback) {
      const { cbId, payload, type } = eventData;

      eventData.payload = typeof payload === 'string' ? safeJsonParse(payload, payload) : payload;

      switch (type) {
        case 'getFirstMessage':
        case 'getLastMessage':
        case 'queryMessageByCliMsgId':
          if (eventData.payload) {
            eventData.payload = formatMessage(eventData.payload);
          }
          break;
        case 'loadMessages':
          if (eventData.payload) {
            eventData.payload.messages = eventData.payload.messages.map(formatMessage);
          }
          break;
      }

      const [cb, abortSignal] = this.callbackMap.get(cbId);
      if (cb) {
        this.callbackMap.delete(cbId);
        if (!abortSignal?.aborted) {
          cb(eventData);
        }
      } else {
        console.error('Callback not found for cbId: ', cbId);
      }
    } else {
      const { event, data } = eventData;
      this.logger.log('notice:' + event, data);
      switch (event) {
        // Session events
        case 'FullSessionsSnapshot':
          data.sessions.forEach(formatSession);
          break;
        case 'UpdateFullSession':
          formatSession(data.session);
          break;
        // Combine multiple session events into one
        case 'SessionBasicInfoUpdated':
        case 'SessionUnreadCountUpdated':
        case 'SessionPreviewUpdated':
        case 'SessionReceiptPreviewUpdated':
        case 'SessionPreviewStatusChanged':
        case 'SessionAtListUpdated':
        case 'SessionLastReadSequenceUpdated':
        case 'SessionOperateChanged':
          switch (event) {
            case 'SessionReceiptPreviewUpdated':
              data.last_message = {
                receipt_status: data.receipt_status,
                read_state: data.read_state,
                at_read_list: data.at_read_list || [],
              };
              break;
            case 'SessionPreviewStatusChanged':
              data.last_message = {
                status: data.status,
              };
              break;
          }

          formatSession(data);

          // TODO amo: Optimize
          this.mergedSessionData[data.chat_id] = deepMerge(
            this.mergedSessionData[data.chat_id],
            data
          );

          this.dispatchSessionData();

          break;

        // Message events
        case 'SendMessage':
          formatMessage(data.message);
          break;
        case 'NewMessages':
          data.messages.map(formatMessage);
          break;

        // Combine multiple message events into one
        case 'MessageStatusChanged':
        case 'MessageReceiptStatusChanged':
        case 'MessageReadToDestroy':
        case 'ReplyListChanged':
        case 'ReadCountChanged':
        case 'MessageThumbnailLoaded':
        case 'MessageThumbnailLoadingError':
        case 'MessageReactionUpdate':
        case 'MessageEditStateChanged':
        case 'FileTransferComplete':
          switch (event) {
            case 'ReadCountChanged':
              data.read_state = { read_count: data.read_count, total_count: data.total_count };
              break;
            case 'MessageThumbnailLoaded':
              // { chat_id: String, message_id: String, cli_msg_id: String, thumbnail: String }
              data.payload = JSON.stringify({
                thumbnail: data.thumbnail,
                thumbnail_error: null,
              });
              break;
            case 'MessageThumbnailLoadingError':
              data.payload = JSON.stringify({
                thumbnail_error: data.error || 'loaded failed',
              });
              break;
            case 'MessageEditStateChanged':
              data.edit = data.status;

              if (data.edit === MessageEditStatus.Edited) {
                data.payload = data.new_payload;
              }

              delete data.status;
              delete data.new_payload;
              break;
            case 'FileTransferComplete':
              data.payload = data.data;
              delete data.data;
              delete data.file_id;
              delete data.context;
              break;
          }

          formatMessage(data);

          // TODO amo: Optimize

          this.mergedMessageData[data.cli_msg_id] = deepMerge(
            this.mergedMessageData[data.cli_msg_id],
            data
          );

          this.dispatchMessageData();

          break;

        // Group events
        case 'ContactInfoListChanged':
          data.data.forEach((user) => {
            formatUser(user);
          });
          break;
        // Emoji events
        case 'StickerDownloadComplete':
          data.render_data = safeJsonParse(data.render_data, {});
          break;
        // Delete msg
        case 'DeleteMessages':
          data.cli_msg_ids.forEach((cli_msg_id) => {
            new MsgFileCache(cli_msg_id).deleteFile();
          });
          break;
      }
      this.dispatchEvent('notice', eventData);
      this.dispatchEvent(event, data);
    }
  }

  sendWorkerMessage<K extends SdkMethod, T = any>(
    type: K,
    payload: any,
    abortSignal?: AbortSignal
  ): Promise<T> {
    const cbId = `${type as string}_${this.callbackId++}`;
    return new Promise((resolve, reject) => {
      const logCb = this.logger?.asyncLog(cbId, payload);
      this.callbackMap.set(cbId, [
        (result) => {
          if (result.error) {
            reject(result.error);
            this.dispatchEvent('error', result.error);
          } else {
            resolve(result.payload);
          }
          logCb?.(result);
        },
        abortSignal,
      ]);

      const abortHandler = () => {
        reject(new Client.AbortError());
      };

      if (abortSignal) {
        abortSignal.addEventListener('abort', abortHandler);
        if (abortSignal.aborted) {
          abortHandler();
        }
      }
      // TODO amo: Abort core sdk
      this.worker?.postMessage({ cbId, type, payload });
    });
  }

  async invoke<K extends WasmSdkFnKeys, T extends Record<string, any> = Record<string, any>>(
    type: K,
    args?: WasmSdkArgsMap[K],
    abortSignal?: AbortSignal
  ): Promise<Awaited<WasmSdkResultMap<T>[K]>> {
    await this.initPromise;
    return this.sendWorkerMessage<K, WasmSdkResultMap<T>[K]>(type, { args }, abortSignal) as any;
  }

  formatMessage<T extends Record<string, any> = Record<string, any>>(message: OriginalMsg) {
    if (message.payload) {
      // @ts-expect-error Override type
      message.payload = safeJsonParse(message.payload, {
        content: message.payload,
        entities: [],
      });
    }

    if (message.msg_ext) {
      message.msg_ext = safeJsonParse(message.msg_ext, {
        forwardUid: message.msg_ext.forwardUid,
      });
    }

    if (!message.message_id) {
      delete message.message_id;
    }

    if (!message.cli_msg_id) {
      delete message.cli_msg_id;
    }

    return message as unknown as Msg<T>;
  }

  formatSession(session: OriginalSession): Session {
    const isGroup = isSessionCategory(session.chat_id, SessionCategoryPrefix.Group);

    if (session.user_info) {
      session.user_info = formatUser(session.user_info);
    }

    if (session.chat_id && !isGroup && session.user_info) {
      session.avatar = session.user_info.avatar;
    }

    if (session.last_message) {
      // @ts-expect-error Override type
      session.last_message = this.formatMessage(session.last_message);
    }

    if (session.last_message?.created_at) {
      session.update_at = session.last_message.created_at;
    } else if (session.created_at && !session.update_at) {
      session.update_at = session.created_at;
    }

    if ('operate' in session) {
      // @ts-expect-error Override type
      session.operateAccess = {};
      Object.entries(SessionOperate).forEach(([key, value]) => {
        let value_ = Number(value);
        value_ = isNaN(value_) ? Number(key) : value_;
        // @ts-expect-error Override type
        session.operateAccess[key] = (session.operate & value_) !== 0;
      });
    }

    return session as unknown as Session;
  }

  [Symbol.dispose](): void {
    this.removeAllEventListeners();
    this.worker?.terminate();
    this.Api?.[Symbol.dispose]();
    this.serverTime?.[Symbol.dispose]?.();
  }
}
